import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import axios from 'axios'

import AsyncSelect from '../../common/AsyncSelect'
import { closeModal } from '../../common/GlobalModal/redux/GlobalModalActions'
import { notificationDanger, notificationSuccess } from '../../utils/customToastify'
import useSendSms from './useSendSms'

const MAX_SMS_LENGTH = 1600

const formatPhoneNumber = (phone) => {
  if (!phone) return ''
  const cleaned = `${phone}`.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

/**
 * Kompozicija nove SMS poruke iz header widgeta: rucni unos broja ILI pretraga
 * kontakta (server vraca samo kontakte kojima posiljalac sme da pise —
 * /ringcentral-sms/contact-search) pa izbor jednog od njegovih brojeva.
 */
const NewSmsModal = () => {
  const dispatch = useDispatch()
  const [selectedContact, setSelectedContact] = useState(null)
  const [toNumber, setToNumber] = useState('')
  const [manualNumber, setManualNumber] = useState('')
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const sendSms = useSendSms()

  const loadContactOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) return { options: [], hasMore: false }
    try {
      const response = await axios.get('/ringcentral-sms/contact-search', { params: { q: inputValue.trim() } })
      return {
        options: (response.data.data || []).map((contact) => ({
          value: contact.id,
          label: contact.name,
          contact,
        })),
        hasMore: false,
      }
    } catch (error) {
      return { options: [], hasMore: false }
    }
  }

  const phoneOptions = useMemo(() => {
    if (!selectedContact) return []
    return [
      { label: 'Mobile', value: selectedContact.mobilePhone },
      { label: 'Business', value: selectedContact.businessPhone },
      { label: 'Home', value: selectedContact.homePhone },
    ].filter((option) => option.value)
  }, [selectedContact])

  const onContactChange = (option) => {
    const contact = option?.contact || null
    setSelectedContact(contact)
    if (contact) {
      const firstPhone = contact.mobilePhone || contact.businessPhone || contact.homePhone || ''
      setToNumber(firstPhone)
    } else {
      setToNumber('')
    }
  }

  const effectiveNumber = selectedContact ? toNumber : manualNumber

  const send = async () => {
    if (!effectiveNumber) return notificationDanger('Enter a phone number or pick a contact')
    if (!text.trim()) return notificationDanger('Message text is required')

    try {
      setSending(true)
      // P2P send from the user's own RC number (native RC Text style)
      await sendSms({
        contactId: selectedContact ? selectedContact.id : null,
        toNumber: effectiveNumber,
        text: text.trim(),
      })
      notificationSuccess('SMS sent')
      dispatch(closeModal('sms_new_message_modal'))
    } catch (error) {
      notificationDanger(error.response?.data?.message || error.message || 'Failed to send SMS')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className='p-3'>
      <Row>
        <Col md='7'>
          <AsyncSelect
            label='Find contact (optional)'
            isClearable
            onChange={onContactChange}
            loadOptions={loadContactOptions}
            name='contact'
            placeholder='Search by name...'
          />
        </Col>
        <Col md='5'>
          {selectedContact ? (
            <FormGroup>
              <Label for='newSmsToNumber'>
                <strong>Send to</strong>
              </Label>
              {phoneOptions.length ? (
                <Input id='newSmsToNumber' type='select' value={toNumber} onChange={(e) => setToNumber(e.target.value)}>
                  {phoneOptions.map((option) => (
                    <option key={option.label} value={option.value}>
                      {option.label}: {formatPhoneNumber(option.value)}
                    </option>
                  ))}
                </Input>
              ) : (
                <p className='text-muted mb-0'>This contact has no phone number.</p>
              )}
            </FormGroup>
          ) : (
            <FormGroup>
              <Label for='newSmsManualNumber'>
                <strong>Or enter a number</strong>
              </Label>
              <Input
                id='newSmsManualNumber'
                type='tel'
                value={manualNumber}
                onChange={(e) => setManualNumber(e.target.value)}
                placeholder='(999) 999-9999'
              />
            </FormGroup>
          )}
        </Col>
      </Row>

      <Input
        type='textarea'
        rows={3}
        placeholder='Type a message...'
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className='d-flex justify-content-between align-items-center mt-2'>
        <small className={text.length > MAX_SMS_LENGTH ? 'text-danger' : 'text-muted'}>
          {text.length}/{MAX_SMS_LENGTH}
        </small>
        <Button
          color='primary'
          onClick={send}
          disabled={!text.trim() || text.length > MAX_SMS_LENGTH || sending || !effectiveNumber}
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </div>
    </div>
  )
}

export default NewSmsModal
